import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import * as accountsService from '../../../services/account';
import * as paymentsService from '../../../services/payments';
import { loadInvoices, loadUsageMetrics, updateAccount } from '../../../actions/account';

import Button from '../../../components/Button';
import Brand from '../../../assets/brand/brand-dark.png';
import InvoicesTable from '../../../components/Tables/Invoices';
import AddCreditModal from '../../../components/Modals/AddCredit';
import UsageMetricsCharts from '../../../components/Charts/UsageMetrics';
import AutoRechargeModal from '../../../components/Modals/AutoRecharge';

import './styles.css';

const Home = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const getCurrentYearMonth = () => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    };

    const [loadingCopy, setLoadingCopy] = useState(false);
    const [openAddCreditModal, setOpenAddCreditModal] = useState(false);
    const [openAutoRechargeModal, setOpenAutoRechargeModal] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(getCurrentYearMonth());

    const account = useSelector((state) => state.accounts.data);

    const monthOptions = useMemo(() => {
        const options = [];
        const now = new Date();
        for (let i = 0; i < 12; i += 1) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = d.toLocaleString('default', { month: 'short' }) + '/' + d.getFullYear();
            options.push({ value, label });
        }
        return options;
    }, []);

    const printAmountTrunc = (raw, { currency = "USD", cents = true } = {}) => {
        if (raw == null) raw = 0;

        // 1️⃣ Converte se estiver em centavos
        const value = cents ? Number(raw) / 100 : Number(raw);

        // 2️⃣ Trunca para 2 casas (mantendo sinal)
        const truncated =
            Math.sign(value) * Math.floor(Math.abs(value) * 100) / 100;

        // 3️⃣ Formata com exatamente 2 casas
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(truncated);
    };

    const getMonthLabel = (yearMonth) => {
        const option = monthOptions.find((o) => o.value === yearMonth);
        return option ? option.label : yearMonth;
    };

    const getInvoices = async () => {
        const response = await paymentsService.getAllPayments(account.uuid);
        if (response) dispatch(loadInvoices(response));
    }

    const handleCopyAccessToken = () => {
        setLoadingCopy(true);
        navigator.clipboard.writeText(JSON.stringify({
            accessToken: account.accessToken,
            uuid: account.uuid
        }))

        setTimeout(() => { setLoadingCopy(false); }, 1000);
    }

    const getAccount = async (yearMonth = selectedMonth, { fullRefresh = false } = {}) => {
        const response = await accountsService.getAccount(account.uuid, yearMonth);
        if (!response) return;

        if (fullRefresh) {
            dispatch(updateAccount(response));
        } else {
            dispatch(loadUsageMetrics({
                usageMetrics: response.usageMetrics,
                monthTotals: response.monthTotals,
                yearMonth: response.yearMonth,
                isClosed: response.isClosed,
            }));
        }
    }

    const handleRefresh = async () => {
        await getAccount(selectedMonth, { fullRefresh: true });
        await getInvoices();
    }

    const handleChangeMonth = (event) => {
        const value = event.target.value;
        setSelectedMonth(value);
        getAccount(value);
    }

    useEffect(() => { 
        handleRefresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account.uuid])

    return (
        <>
            <AutoRechargeModal 
                open={openAutoRechargeModal} 
                onClose={() => setOpenAutoRechargeModal(false)} 
            />
            <AddCreditModal 
                open={openAddCreditModal} // This modal is not used in this component, but can be opened from other parts of the app
                onClose={() => setOpenAddCreditModal(false)} // No action needed here, as it's not used
            />
            <div className="home-container">
                <div className='home-content'>
                    <div className='home-header'>
                        <img src={Brand} alt=""/>
                        <div style={{ display: 'flex', gap: 32 }}>
                            <Button 
                                kind="link"
                                label="Models & Pricing"
                                onClick={() => navigate('/models')}
                                style={{ padding: 0, width: 'auto' }}
                            />
                            <Button 
                                kind="link"
                                label="Documentation"
                                onClick={() => window.open('https://papayia.readme.io/', '_blank')}
                                style={{ padding: 0, width: 'auto' }}
                            />
                        </div>
                    </div>
                    <div className='home-body'>
                        <div className='home-body-item'>{/* Credentials */}
                            <h4>Credentials</h4>
                            <div className='home-access-token'>
                                <div className='home-access-token-info'>
                                    <span><strong>Access Token:</strong> {account.accessToken}</span>
                                    <span><strong>Client ID:</strong> {account.uuid}</span>
                                </div>
                                <Button 
                                    kind="link"
                                    label={loadingCopy ? "Copying..." : "Copy"}
                                    style={{ width: 'auto', display: 'flex', justifyContent: 'end', padding: 0 }}
                                    onClick={handleCopyAccessToken}
                                />
                            </div>
                        </div>
                        <div className='home-body-item'>{/* Usage Metrics */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h4>Usage Metrics</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                    <select
                                        value={selectedMonth}
                                        onChange={handleChangeMonth}
                                        className='home-month-select'
                                    >
                                        {monthOptions.map((o) => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                    <Button
                                        kind="link"
                                        label="Refresh metrics"
                                        onClick={handleRefresh}
                                        style={{ padding: 0, width: 'auto' }}
                                    />
                                </div>
                            </div>
                            <UsageMetricsCharts
                                yearMonth={selectedMonth}
                                monthLabel={getMonthLabel(selectedMonth)}
                                monthTotals={account.monthTotals}
                                isClosed={selectedMonth !== getCurrentYearMonth()}
                                usageMetrics={account.usageMetrics}
                            />
                        </div>
                        <div className='home-body-item'>{/* Credits */}
                            <h4>Credit balance</h4>
                            <span className='home-credit-balance'>{printAmountTrunc(account.credits, { cents: true})}</span>
                            <div className='home-credit-actions'>
                                <Button 
                                    label="Add to credit balance"
                                    style={{ flex: 0.5 }}
                                    onClick={() => setOpenAddCreditModal(true)}
                                />
                            </div>
                            <div className='home-credit-auto-recharge'>
                                <strong>{account.autoRechargeEnabled ? "Auto recharge is enabled" : "Auto recharge is disabled"}</strong>
                                <p>{
                                    `
                                    When your credit balance reaches ${printAmountTrunc(account.autoRechargeThreshold)}, your payment method will be charged to bring 
                                    the balance up to ${printAmountTrunc(account.autoRechargeAmount)}. The maximum amount of credits that will automatically recharge 
                                    in a month is ${printAmountTrunc(account.autoRechargeLimitAmount)}.
                                    `    
                                }</p>
                                <div className='home-credit-actions'>
                                    <Button 
                                        label="Modify"
                                        style={{ flex: 0.5 }}
                                        onClick={() => setOpenAutoRechargeModal(true)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='home-body-item'>{/* Credits */}
                            <h4>Invoices</h4>
                            <InvoicesTable
                                invoices={account.invoices}
                            />
                        </div>
                    </div>
                </div>
            </div>  
        </>
    );
}

export default Home;